import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  Chip,
  Divider,
} from '@mui/joy';
import {
  Calendar as CalendarIcon,
  Clock as ScheduleIcon,
  User as PersonIcon,
  Envelope as EmailIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
} from '@phosphor-icons/react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { schedulingApi } from '../../lib/apiScheduling';
import type { TimeSlot, BookingRequest } from '../../types/scheduling';
import { formatTimeSlot, groupSlotsByDate } from '../../types/scheduling';

export const BookingWidget: React.FC = () => {
  const { orgSlug, username, teamSlug } = useParams<{ orgSlug: string; username: string; teamSlug: string }>();
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [confirmedMeeting, setConfirmedMeeting] = useState<any>(null);

  // Form state
  const [inviteeName, setInviteeName] = useState('');
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [inviteePhone, setInviteePhone] = useState('');
  const [inviteeTimezone, setInviteeTimezone] = useState('America/New_York');
  const [inviteeNotes, setInviteeNotes] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Detect user's timezone
  useEffect(() => {
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setInviteeTimezone(detectedTimezone);
  }, []);

  // Fetch availability
  const { data: availabilityData, isLoading, error } = useQuery({
    queryKey: ['publicAvailability', orgSlug, username, teamSlug],
    queryFn: () => teamSlug ? schedulingApi.getTeamAvailability(orgSlug!, teamSlug) : schedulingApi.getUserAvailability(orgSlug!, username!, 14),
    enabled: !!orgSlug && (!!username || !!teamSlug),
  });

  // Confirm booking mutation
  const confirmMutation = useMutation({
    mutationFn: (data: BookingRequest) =>
      teamSlug ? schedulingApi.confirmTeamBooking(orgSlug!, teamSlug, data) : schedulingApi.confirmBooking(orgSlug!, username!, data),
    onSuccess: (data) => {
      setBookingConfirmed(true);
      setConfirmedMeeting(data);
    },
  });

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!inviteeName.trim()) {
      errors.name = 'Name is required';
    }
    if (!inviteeEmail.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteeEmail)) {
      errors.email = 'Invalid email format';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSlotSelect = (slot: TimeSlot) => {
    setSelectedSlot(slot);
    setFormErrors({});
  };

  const handleConfirmBooking = () => {
    if (!selectedSlot) return;

    if (!validateForm()) {
      return;
    }

    confirmMutation.mutate({
      invitee_name: inviteeName,
      invitee_email: inviteeEmail,
      invitee_phone: inviteePhone,
      invitee_timezone: inviteeTimezone,
      invitee_notes: inviteeNotes,
      start_time: selectedSlot.start_time,
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.level1' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !availabilityData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.level1', p: 3 }}>
        <Card variant="outlined" sx={{ maxWidth: 600 }}>
          <CardContent>
            <Stack alignItems="center" spacing={2}>
              <CalendarIcon size={64} color="var(--joy-palette-text-secondary)" />
              <Typography level="h4">Booking Not Available</Typography>
              <Typography level="body-md" textAlign="center">
                This booking page is not available or has been disabled.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Booking confirmed view
  if (bookingConfirmed && confirmedMeeting) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.level1', p: 3 }}>
        <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
          <Card variant="outlined" color="success">
            <CardContent>
              <Stack alignItems="center" spacing={3}>
                <CheckCircleIcon size={80} color="var(--joy-palette-success-500)" />
                <Typography level="h3">Booking Confirmed!</Typography>
                <Typography level="body-lg" textAlign="center">
                  Your meeting with <strong>{availabilityData.user.name}</strong> has been scheduled.
                </Typography>

                <Divider sx={{ width: '100%' }} />

                <Stack spacing={2} sx={{ width: '100%' }}>
                  <Box>
                    <Typography level="body-xs" color="neutral">MEETING TITLE</Typography>
                    <Typography level="title-md">{confirmedMeeting.title}</Typography>
                  </Box>

                  <Box>
                    <Typography level="body-xs" color="neutral">DATE & TIME</Typography>
                    <Typography level="title-md">
                      {new Date(confirmedMeeting.start_time).toLocaleString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      })}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography level="body-xs" color="neutral">DURATION</Typography>
                    <Typography level="title-md">{confirmedMeeting.duration_minutes} minutes</Typography>
                  </Box>

                  {confirmedMeeting.location && (
                    <Box>
                      <Typography level="body-xs" color="neutral">LOCATION</Typography>
                      <Typography level="title-md">{confirmedMeeting.location}</Typography>
                    </Box>
                  )}

                  <Alert color="success" size="sm">
                    <Stack spacing={0.5}>
                      <Typography level="body-sm" fontWeight="bold">
                        Calendar invite sent!
                      </Typography>
                      <Typography level="body-xs">
                        Check your email at <strong>{inviteeEmail}</strong> for a calendar invitation and meeting details.
                      </Typography>
                    </Stack>
                  </Alert>

                  {confirmedMeeting.cancellation_url && (
                    <Typography level="body-xs" color="neutral" textAlign="center">
                      Need to reschedule? Use the link in your email confirmation.
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  const groupedSlots = groupSlotsByDate(availabilityData.slots);
  const dateKeys = Object.keys(groupedSlots);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.level1', p: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4 }}>
        {/* Header */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={3} alignItems="center">
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'var(--brand-text, #7013f5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PersonIcon size={48} color="white" />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography level="h3">{availabilityData.user.name}</Typography>
                <Typography level="title-lg" color="primary">
                  {availabilityData.user.meeting_title}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                  <Chip startDecorator={<ScheduleIcon />} size="sm">
                    {availabilityData.user.duration_minutes} minutes
                  </Chip>
                  {availabilityData.user.meeting_location && (
                    <Chip size="sm" variant="outlined">
                      {availabilityData.user.meeting_location}
                    </Chip>
                  )}
                </Stack>
                {availabilityData.user.welcome_message && (
                  <Typography level="body-sm" sx={{ mt: 2 }}>
                    {availabilityData.user.welcome_message}
                  </Typography>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          {/* Left - Available Slots */}
          <Box sx={{ flex: 1 }}>
            <Typography level="h4" sx={{ mb: 2 }}>
              Select a Time
            </Typography>

            {dateKeys.length === 0 ? (
              <Card variant="outlined">
                <CardContent>
                  <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                    <CalendarIcon size={64} color="var(--joy-palette-text-secondary)" />
                    <Typography level="title-lg">No Available Times</Typography>
                    <Typography level="body-sm" color="neutral" textAlign="center">
                      There are no available time slots at the moment. Please check back later or contact directly.
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ) : (
              <Stack spacing={3}>
                {dateKeys.map((dateKey) => (
                  <Box key={dateKey}>
                    <Typography level="title-md" sx={{ mb: 1.5 }}>
                      {dateKey}
                    </Typography>
                    <Stack spacing={1}>
                      {groupedSlots[dateKey].map((slot, idx) => (
                        <Button
                          key={idx}
                          variant={selectedSlot?.start_time === slot.start_time ? 'solid' : 'outlined'}
                          onClick={() => handleSlotSelect(slot)}
                          sx={{ justifyContent: 'flex-start' }}
                        >
                          {formatTimeSlot(slot)}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {/* Right - Booking Form */}
          {selectedSlot && (
            <Card variant="outlined" sx={{ minWidth: 400 }}>
              <CardContent>
                <Typography level="h4" sx={{ mb: 2 }}>
                  Your Information
                </Typography>

                <Stack spacing={2}>
                  <Alert color="primary" size="sm" startDecorator={<CalendarIcon />}>
                    {new Date(selectedSlot.start_time).toLocaleString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </Alert>

                  <FormControl error={!!formErrors.name}>
                    <FormLabel>Name *</FormLabel>
                    <Input
                      value={inviteeName}
                      onChange={(e) => setInviteeName(e.target.value)}
                      placeholder="Your full name"
                      startDecorator={<PersonIcon />}
                    />
                    {formErrors.name && (
                      <Typography level="body-xs" color="danger">
                        {formErrors.name}
                      </Typography>
                    )}
                  </FormControl>

                  <FormControl error={!!formErrors.email}>
                    <FormLabel>Email *</FormLabel>
                    <Input
                      type="email"
                      value={inviteeEmail}
                      onChange={(e) => setInviteeEmail(e.target.value)}
                      placeholder="your@email.com"
                      startDecorator={<EmailIcon />}
                    />
                    {formErrors.email && (
                      <Typography level="body-xs" color="danger">
                        {formErrors.email}
                      </Typography>
                    )}
                  </FormControl>

                  <FormControl>
                    <FormLabel>Phone (optional)</FormLabel>
                    <Input
                      value={inviteePhone}
                      onChange={(e) => setInviteePhone(e.target.value)}
                      placeholder="(555) 123-4567"
                      startDecorator={<PhoneIcon />}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Timezone</FormLabel>
                    <Select
                      value={inviteeTimezone}
                      onChange={(_, value) => setInviteeTimezone(value as string)}
                    >
                      <Option value="America/New_York">Eastern Time</Option>
                      <Option value="America/Chicago">Central Time</Option>
                      <Option value="America/Denver">Mountain Time</Option>
                      <Option value="America/Los_Angeles">Pacific Time</Option>
                      <Option value="Europe/London">London</Option>
                      <Option value="Europe/Paris">Paris</Option>
                      <Option value="Asia/Tokyo">Tokyo</Option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Additional Notes (optional)</FormLabel>
                    <Textarea
                      value={inviteeNotes}
                      onChange={(e) => setInviteeNotes(e.target.value)}
                      placeholder="Anything you'd like to share..."
                      minRows={3}
                    />
                  </FormControl>

                  {confirmMutation.error && (
                    <Alert color="danger" size="sm">
                      Failed to book meeting. The time slot may no longer be available.
                    </Alert>
                  )}

                  <Button
                    size="lg"
                    onClick={handleConfirmBooking}
                    loading={confirmMutation.isPending}
                  >
                    Confirm Booking
                  </Button>

                  <Typography level="body-xs" color="neutral" textAlign="center">
                    By booking, you'll receive a calendar invitation via email
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
